import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, Wallet, CheckCircle2, Loader2 } from "lucide-react";

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return "—"; }
};

export default function AdminStuckDebits() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, unresolved: 0 });
  const [loading, setLoading] = useState(false);
  const [refundingId, setRefundingId] = useState(null);
  const [showResolved, setShowResolved] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stuck-wallet-debits");
      setItems(data.items || []);
      setStats({ total: data.total || 0, unresolved: data.unresolved || 0 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load stuck debits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refund = async (ledger_id) => {
    setRefundingId(ledger_id);
    try {
      const { data } = await api.post(`/admin/stuck-wallet-debits/${ledger_id}/refund`);
      toast.success(`Refunded · ${data.wallet} wallet new balance ₹${data.new_balance.toFixed(2)}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refund failed");
    } finally {
      setRefundingId(null);
    }
  };

  const visible = showResolved ? items : items.filter((i) => !i.resolved);

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin · Reconciliation</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Stuck wallet debits
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
            Wallet debit ledger entries whose <code className="font-mono bg-accent px-1 py-0.5 rounded">ref_id</code> doesn't match any <code className="font-mono bg-accent px-1 py-0.5 rounded">transactions</code> row. These are funds debited but with no transaction recorded — orphans from the historical "Submit fails after debit" bug. Click <strong>Refund</strong> to credit the wallet back.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-stuck">
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Reload
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="surface-card p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Total found</div>
          <div className="font-display text-2xl font-bold mt-1" data-testid="stat-total">{stats.total}</div>
        </div>
        <div className="surface-card p-4 border-l-4 border-l-warning">
          <div className="text-xs uppercase tracking-widest text-warning">Unresolved</div>
          <div className="font-display text-2xl font-bold mt-1" data-testid="stat-unresolved">{stats.unresolved}</div>
        </div>
        <div className="surface-card p-4 border-l-4 border-l-success">
          <div className="text-xs uppercase tracking-widest text-success">Refunded</div>
          <div className="font-display text-2xl font-bold mt-1" data-testid="stat-resolved">{stats.total - stats.unresolved}</div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="show-resolved-toggle">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          Show resolved
        </label>
        <span className="text-xs text-muted-foreground">({visible.length} shown)</span>
      </div>

      <div className="mt-4 surface-card overflow-x-auto" data-testid="stuck-debits-table">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 text-xs uppercase tracking-widest">
            <tr>
              <th className="px-3 py-2 text-left">When (IST)</th>
              <th className="px-3 py-2 text-left">Wallet</th>
              <th className="px-3 py-2 text-left">Who</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Ref</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                {loading ? "Loading…" : (showResolved ? "No stuck debits found" : "No unresolved stuck debits ✓")}
              </td></tr>
            )}
            {visible.map((r) => (
              <tr key={r.ledger_id} className="border-t hover:bg-accent/20" data-testid={`row-${r.ledger_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{fmtIST(r.created_at)}</td>
                <td className="px-3 py-2">
                  {r.wallet_type === "partner" ? (
                    <Badge variant="outline"><Wallet className="h-3 w-3 mr-1" />Partner</Badge>
                  ) : (
                    <Badge variant="outline" className="border-blue-300 text-blue-700"><Wallet className="h-3 w-3 mr-1" />Customer</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.wallet_type === "partner"
                    ? <span>{r.user_name || r.user_email || r.user_id?.slice(0, 8)}</span>
                    : <span className="font-mono">+91 {r.mobile}</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatINR(r.amount)}</td>
                <td className="px-3 py-2 text-xs font-mono">
                  <div>{r.ref_type}</div>
                  <div className="text-muted-foreground">{r.ref_id?.slice(0, 8)}…</div>
                </td>
                <td className="px-3 py-2 text-xs max-w-[260px]">{r.reason}</td>
                <td className="px-3 py-2 text-right">
                  {r.resolved ? (
                    <Badge className="bg-success text-success-foreground"><CheckCircle2 className="h-3 w-3 mr-1" />Refunded</Badge>
                  ) : (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => refund(r.ledger_id)}
                      disabled={refundingId === r.ledger_id}
                      data-testid={`refund-${r.ledger_id}`}
                    >
                      {refundingId === r.ledger_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ShieldAlert className="h-3 w-3 mr-1" />}
                      Refund
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
